import { Tooltip } from "../Tooltip/index.js";
import { ClearButton } from "./ClearButton.js";
import { ErrorContainer } from "./ErrorContainer.js";
import { Container, StyledInput, StyledInputWrapper, StyledLabel, StyledLeftIconWrapper } from "./Input.elements.js";
import React from "react";
import { IconInfo, IconLoader } from "@synthesis/shared-icons";
import { uuidv4 } from "@synthesis/shared-utils";
import { jsx, jsxs } from "react/jsx-runtime";
import { get, useController, useFormContext, useFormState, useWatch } from "react-hook-form";
import { useIMask } from "react-imask";
//#region src/Input/index.tsx
function getResolver(control) {
	return control["_options"]?.resolver;
}
const InputMasked = React.forwardRef(({ name, type = "text", placeholder = "", label, leftIcon, size = "md", disabled = false, clearable = false, readOnly = false, methods, onClear, onBlur, className, required, rules, settings, max, min, isLoading, errorVariant = "substring", ...rest }, ref) => {
	const inputWrapperRef = React.useRef(null);
	const maskedInputElRef = React.useRef(null);
	const { current: fieldId } = React.useRef(`prefix-${uuidv4()}`);
	const fieldState = useFormState({
		control: methods.control,
		name
	});
	const error = get(fieldState.errors, `${name}.message`);
	const { field: { onChange, onBlur: fieldOnBlur, ref: formRef }, fieldState: { isDirty } } = useController({
		name,
		control: methods.control,
		rules
	});
	const { ref: maskInputRef, setValue } = useIMask(settings, {
		onAccept: (_value, mask) => {
			const typedValue = mask.typedValue;
			if (!isDirty && (typedValue === void 0 || typedValue === null || String(typedValue) === "")) return;
			onChange(typedValue);
		},
		onComplete: (_value, mask) => {
			onChange(mask.typedValue);
		}
	});
	const watchedFieldValue = useWatch({
		name,
		control: methods.control
	});
	const hasResolver = Boolean(getResolver(methods.control));
	const onReset = React.useCallback(() => {
		methods.clearErrors(name);
		methods.setValue(name, "", { shouldValidate: true });
		onClear?.();
	}, [
		name,
		methods,
		onClear
	]);
	React.useImperativeHandle(ref, () => maskedInputElRef.current, []);
	React.useEffect(() => {
		if (watchedFieldValue !== void 0) setValue(String(watchedFieldValue ?? ""));
	}, [setValue, watchedFieldValue]);
	return /* @__PURE__ */ jsxs(Container, {
		className,
		children: [
			label && /* @__PURE__ */ jsxs(StyledLabel, {
				disabled,
				htmlFor: fieldId,
				children: [label, required && /* @__PURE__ */ jsx("span", {
					className: "text-red-600",
					children: " *"
				})]
			}),
			/* @__PURE__ */ jsxs(StyledInputWrapper, {
				"data-cy": "inputComponent-styledInputWrapper",
				ref: inputWrapperRef,
				size,
				disabled,
				readOnly,
				withError: Boolean(error),
				children: [
					leftIcon && /* @__PURE__ */ jsx(StyledLeftIconWrapper, {
						size,
						children: leftIcon
					}),
					/* @__PURE__ */ jsx(StyledInput, {
						"data-cy": "inputComponent-styledInput",
						readOnly,
						placeholder,
						type,
						max,
						min,
						disabled,
						id: fieldId,
						...rest,
						ref: (element) => {
							formRef(element);
							maskedInputElRef.current = element;
							maskInputRef.current = element;
						},
						onBlur: (event) => {
							fieldOnBlur();
							onBlur?.(event);
						}
					}),
					clearable && !readOnly && /* @__PURE__ */ jsx(ClearButton, {
						name,
						control: methods.control,
						onReset
					}),
					error && !isLoading && errorVariant === "tooltip" && /* @__PURE__ */ jsx(Tooltip, {
						description: String(error),
						arrowSide: "top",
						arrowAlign: "end",
						children: /* @__PURE__ */ jsx("span", {
							"aria-label": String(error),
							className: "text-system-error-50 inline-flex size-5 items-center justify-center text-sm leading-none",
							children: /* @__PURE__ */ jsx(IconInfo, { className: "size-5" })
						})
					}),
					isLoading && /* @__PURE__ */ jsx(IconLoader, {
						className: "size-5 animate-spin",
						"aria-hidden": true
					})
				]
			}),
			!readOnly && hasResolver && errorVariant === "substring" && /* @__PURE__ */ jsx(ErrorContainer, {
				inputRef: inputWrapperRef,
				control: methods.control,
				name,
				error: error ?? ""
			})
		]
	});
});
InputMasked.displayName = "InputMasked";
const InputBase = React.forwardRef(({ name, type = "text", placeholder = "", label, leftIcon, size = "md", disabled = false, clearable = false, readOnly = false, methods, onClear, onBlur, className, required, rules, max, min, isLoading, errorVariant = "substring", ...rest }, ref) => {
	const inputWrapperRef = React.useRef(null);
	const inputRef = React.useRef(null);
	const { current: fieldId } = React.useRef(`prefix-${uuidv4()}`);
	const fieldState = useFormState({
		control: methods.control,
		name
	});
	const error = get(fieldState.errors, `${name}.message`);
	const hasResolver = Boolean(getResolver(methods.control));
	const { ref: registerRef, onBlur: registerOnBlur, ...registerProps } = methods.register(name, rules);
	const onReset = () => {
		methods.clearErrors(name);
		methods.setValue(name, "", { shouldValidate: true });
		onClear?.();
	};
	React.useImperativeHandle(ref, () => inputRef.current, []);
	return /* @__PURE__ */ jsxs(Container, {
		className,
		children: [
			label && /* @__PURE__ */ jsxs(StyledLabel, {
				disabled,
				htmlFor: fieldId,
				children: [label, required && /* @__PURE__ */ jsx("span", {
					className: "text-red-600",
					children: " *"
				})]
			}),
			/* @__PURE__ */ jsxs(StyledInputWrapper, {
				ref: inputWrapperRef,
				size,
				disabled,
				readOnly,
				withError: Boolean(error),
				children: [
					leftIcon && /* @__PURE__ */ jsx(StyledLeftIconWrapper, {
						size,
						children: leftIcon
					}),
					/* @__PURE__ */ jsx(StyledInput, {
						"data-cy": "inputComponent-styledInput",
						readOnly,
						placeholder,
						type,
						max,
						min,
						disabled,
						id: fieldId,
						...registerProps,
						...rest,
						ref: (element) => {
							registerRef(element);
							inputRef.current = element;
						},
						onBlur: (event) => {
							registerOnBlur(event);
							onBlur?.(event);
						}
					}),
					clearable && !readOnly && /* @__PURE__ */ jsx(ClearButton, {
						name,
						control: methods.control,
						onReset
					}),
					error && !isLoading && errorVariant === "tooltip" && /* @__PURE__ */ jsx(Tooltip, {
						description: String(error),
						arrowSide: "top",
						arrowAlign: "end",
						children: /* @__PURE__ */ jsx("span", {
							"aria-label": String(error),
							className: "text-system-error-50 inline-flex size-5 items-center justify-center text-sm leading-none",
							children: /* @__PURE__ */ jsx(IconInfo, { className: "size-5" })
						})
					}),
					isLoading && /* @__PURE__ */ jsx(IconLoader, {
						className: "size-5 animate-spin",
						"aria-hidden": true
					})
				]
			}),
			!readOnly && hasResolver && errorVariant === "substring" && /* @__PURE__ */ jsx(ErrorContainer, {
				inputRef: inputWrapperRef,
				control: methods.control,
				name,
				error: error ?? ""
			})
		]
	});
});
InputBase.displayName = "InputBase";
const Input = React.forwardRef((props, ref) => {
	const methods = useFormContext();
	if (props.settings) return /* @__PURE__ */ jsx(InputMasked, {
		...props,
		settings: props.settings,
		methods,
		ref
	});
	return /* @__PURE__ */ jsx(InputBase, {
		...props,
		methods,
		ref
	});
});
Input.displayName = "Input";
//#endregion
export { Input };

//# sourceMappingURL=index.js.map